import { apiFetch } from './client.js';

// =============================================================
// Tipos
// =============================================================

export type ApuIaRunStatus = 'queued' | 'running' | 'completed' | 'failed' | 'canceled';

export interface ApuIaRun {
  id: string;
  triggered_by: string | null;
  triggered_at: string;
  finished_at: string | null;
  status: ApuIaRunStatus;
  conceptos_total: number;
  conceptos_ok: number;
  conceptos_partial: number;
  conceptos_failed: number;
  tokens_input_total: number;
  tokens_output_total: number;
  cost_usd_total: number;
  /** Detalle técnico del fallo (solo staff/logs; la pantalla no lo muestra al cliente). */
  error_message: string | null;
  /** Categoría CLIENT-SAFE del fallo: 'ai_unavailable' (servicio de IA caído) | 'other'. */
  fail_reason: string | null;
}

export interface ApuIaFactorAplicado {
  id: string;
  dispara: boolean;
  ajuste_pct: number;
  justificacion: string;
  /** Referencia al libro ECOSTOS (ej. "Cap. 2 Tabla 2.1"). Null si no aplica. */
  fuente?: string | null;
  /** Componente del APU sobre el que pega: mano_obra / equipo / materiales (F2/D5). */
  pega_en?: string[];
  /** Frase exacta del concepto/documento que disparó el factor (F2/D11). */
  evidencia?: string | null;
  /** Confianza de la inferencia del factor (alimenta el "confírmalo" de la revisión). */
  confianza_inferencia?: 'alta' | 'media' | 'baja' | null;
}

/**
 * DE DÓNDE SALIÓ EL PRECIO DE UN INSUMO — los campos que el motor de precios agrega al
 * renglón del análisis después de generarlo. Viajan dentro de `apu_ia_matrix` (jsonb crudo)
 * y hasta ahora no estaban declarados en ningún tipo: cada pantalla que los necesitaba se
 * los inventaba con un cast. La regla que los interpreta vive en `@supernova/shared`
 * (`origenDelPrecio`), para que el análisis del presupuesto y la pantalla interna de
 * Ecosoft cuenten la misma historia.
 */
export interface ApuIaPrecioDelRenglon {
  /** `catalogo` | `mercado` | `missing` | `auxiliar` | `costo_horario` | `manual`. */
  fuente?: string;
  precio_unitario?: number | null;
  /** Concepto del catálogo del cliente del que se tomó el precio. */
  clave_catalogo?: string;
  descripcion_catalogo?: string;
  /** Cómo se vende el recurso en el mercado (ej. "bulto 25 kg"). */
  presentacion_mercado?: string;
  /** Cómo se llegó al número, en una frase. */
  nota_mercado?: string;
  /** Comercios que respaldan el precio. Vacía = no hay ninguno que mostrar. */
  fuentes_mercado?: string[];
}

export interface ApuIaCuadrillaComponente extends ApuIaPrecioDelRenglon {
  categoria: string;
  cantidad: number;
}

export interface ApuIaCuadrilla {
  tipo_cuadrilla: string;
  componentes: ApuIaCuadrillaComponente[];
}

export interface ApuIaEquipo extends ApuIaPrecioDelRenglon {
  descripcion_normalizada: string;
  /** Horas de la máquina por UNIDAD del concepto (campo vigente). */
  horas_uso_por_unidad?: number;
  /** LEGACY: horas por jornada (APU anteriores al 2026-08-11). */
  tiempo_uso_hr_por_jornada?: number;
  estado?: 'activo' | 'en_espera';
  /** Forma alterna (APUs de ECOSTOS/biblioteca): usan `descripcion` + `cantidad`
   * en vez de `descripcion_normalizada` + `tiempo_uso_hr_por_jornada`. */
  descripcion?: string;
  cantidad?: number;
  unidad?: string;
}

export interface ApuIaSubReceta {
  rendimiento_base: number;
  rendimiento_base_unidad: string;
  cuadrilla?: ApuIaCuadrilla;
  materiales: Array<ApuIaPrecioDelRenglon & {
    tipo: 'material' | 'agua';
    descripcion_normalizada: string;
    unidad: string;
    cantidad_neta: number;
    desperdicio_pct?: number;
  }>;
  equipo?: ApuIaEquipo[];
  herramienta_pct?: number;
}

export interface ApuIaInsumo extends ApuIaPrecioDelRenglon {
  tipo: 'material' | 'auxiliar' | 'agua';
  descripcion_normalizada: string;
  unidad: string;
  cantidad_neta: number;
  desperdicio_pct?: number;
  ref_auxiliar_id?: string;
  sub_receta?: ApuIaSubReceta;
}

export interface ApuIaMatrix {
  id_concepto: string;
  /** Traza de edición manual (2026-07-03): la UI muestra el chip "Editado
   * manualmente" y avisa al regenerar que se perderán las ediciones. */
  editado_manual?: { at: string; by: string };
  /** Self-critique: alcances que el enunciado nombra y cómo quedaron cubiertos. */
  razonamiento_alcance?: string;
  /** Self-critique de Grok (propuesta A): por qué eligió esas categorías de cuadrilla. */
  razonamiento_cuadrilla?: string;
  /** Self-critique de Grok: por qué incluyó / omitió equipo. */
  razonamiento_equipo?: string;
  /** Self-critique de Grok: cómo el proceso afecta los desperdicios. */
  razonamiento_desperdicios?: string;
  rendimiento_base: number;
  rendimiento_base_unidad: string;
  cuadrilla: ApuIaCuadrilla;
  materiales: ApuIaInsumo[];
  equipo?: ApuIaEquipo[];
  herramienta_pct?: number;
  factores_aplicados: ApuIaFactorAplicado[];
  alertas_levantadas?: string[];
  preguntas_pendientes?: string[];
  memoria_decisiones?: string;
}

export interface ApuIaConcepto {
  id: string;
  idx: number;
  clave: string | null;
  descripcion: string;
  unidad: string;
  cantidad: number;
  apu_ia_rendimiento_base: number | null;
  apu_ia_rendimiento_ajustado: number | null;
  apu_ia_alertas: string[] | null;
  apu_ia_preguntas_pendientes: string[] | null;
  apu_ia_factores_aplicados: ApuIaFactorAplicado[] | null;
  apu_ia_matrix: ApuIaMatrix | null;
  apu_ia_confianza_semaforo: 'green' | 'yellow' | 'red' | null;
  apu_ia_confianza_razon: string | null;
  apu_ia_atomicos_total: number | null;
  apu_ia_atomicos_match: number | null;
  apu_ia_atomicos_missing: number | null;
  apu_ia_sub_materiales: number | null;
  apu_ia_sub_mo: number | null;
  apu_ia_sub_equipo: number | null;
  apu_ia_sub_herramienta: number | null;
  apu_ia_sr_cuadrilla: number | null;
  apu_ia_costo_directo: number | null;
  apu_ia_importe: number | null;
  apu_ia_review_status: 'pending' | 'approved' | 'rejected' | 'edited' | null;
  apu_ia_reviewed_at: string | null;
  apu_ia_cotejo_origen: 'alta' | 'media' | 'sin_candidato' | null;
  apu_ia_validaciones: Array<{ id: string; tipo: 'success' | 'warning' | 'danger'; msg: string; critica?: boolean }> | null;
  apu_ia_context_outdated: boolean | null;
  /** true si el APU se reutilizó de la biblioteca (estadística + leyenda staff). */
  apu_ia_from_library?: boolean | null;
}

export interface ApuIaRunDetail {
  run: ApuIaRun;
  conceptos: ApuIaConcepto[];
  /** Conceptos marcados con el toggle "Cotizar con IA" que aún NO tienen APU
   *  generado → alimenta el botón "Generar nuevos (N)". */
  pendingApuIa?: number;
}

export interface ObraContext {
  // 18 campos del contexto de proyecto
  /** País de la obra (ISO 3166-1 alpha-2, ej. 'MX', 'CO'). Ancla la moneda del
   *  presupuesto y el país de búsqueda de precios de mercado (Serper gl). */
  pais?: string;
  /** Moneda del presupuesto (ISO 4217, ej. 'MXN', 'COP'). Etiqueta los precios
   *  de mercado; conversión de tipo de cambio diferida. */
  moneda?: string;
  ciudad?: string;
  altitud_msnm?: number;
  temperatura_promedio_jornada_c?: number;
  humedad_mayor_80_pct?: boolean;
  trabajo_exterior_en_temporada_lluvias?: boolean;
  zona_costera_pacifico_julio_octubre?: boolean;
  zona_centro_julio_septiembre?: boolean;
  tipo_obra?: 'publica' | 'privada';
  /** Modelo de costo de los equipos sintéticos. 'auto' (default) lo deriva del
   *  tipo de obra (pública → costo horario, privada → renta); 'costo_horario' /
   *  'renta' lo fuerzan sin importar el tipo de obra. */
  modelo_costo_equipo?: 'auto' | 'costo_horario' | 'renta';
  /** Disciplina de obra: decide qué factores aplican (gating, spec §5.4). */
  disciplina?: 'edificacion' | 'civil_pesada' | 'industrial' | 'urbanizacion'
    | 'hidraulica' | 'electromecanica' | 'mixta';
  marco_normativo?: string;
  /** Entidad CONVOCANTE (dependencia/empresa que convoca), inferida del pliego. */
  entidad?: string;
  /** Entidad federativa (estado) del sitio — separación §7 spec ayuda contextual. */
  estado?: string;
  nivel_sobre_planta_baja?: number;
  acceso?: 'normal' | 'restringido';
  espacio?: 'normal' | 'reducido';
  turno?: 'diurno' | 'nocturno' | 'extra_continuo';
  geometria?: 'estandar' | 'irregular';
  supervision?: 'permanente' | 'parcial' | 'ausente';
  experiencia_cuadrilla?: 'exp_con_maestro' | 'mixta' | 'nueva';
  horas_espera_promedio_por_jornada?: number;
  unidades_iguales_consecutivas?: number;
  lluvia_concepto?: boolean;
  _meta?: {
    deducido_de_obra_analysis?: string[];
    override_usuario?: string[];
    version_form?: string;
  };
  // Extensible para más campos
  [key: string]: unknown;
}

export interface FactorSeleccion {
  id: string;
  activo: boolean;
  /** Si está presente, sobrescribe el default del catálogo. */
  override_pct?: number | null;
}

export interface ObraContextRecord {
  id: string;
  context: ObraContext;
  factores_seleccion: FactorSeleccion[];
  source: 'deduced' | 'manual' | 'mixed';
  created_at: string;
  created_by: string | null;
}

// =============================================================
// Funciones API
// =============================================================

export async function generateApuIa(
  quoteId: string,
  // `confirmarParcial`: el usuario ya vio que su bolsa no alcanza para todo el lote y
  // aceptó que se genere hasta donde alcance. Sin esto el servidor responde 409 y no
  // encola nada, que es lo que queremos: preguntar antes de gastar.
  opts: { maxConcepts?: number; conceptIds?: string[]; confirmarParcial?: boolean } = {},
): Promise<{ runId: string; jobId: string; conceptos_total: number; status: string }> {
  const body: Record<string, unknown> = {};
  if (opts.maxConcepts != null) body.maxConcepts = opts.maxConcepts;
  if (opts.conceptIds && opts.conceptIds.length > 0) body.conceptIds = opts.conceptIds;
  if (opts.confirmarParcial) body.confirmarParcial = true;
  return await apiFetch(`/api/v1/quotes/${quoteId}/apu-ia/generate`, { method: 'POST', body });
}

/** Cambia la fuente activa del precio de un concepto (instantáneo, $0, sin IA):
 *  'ia' activa el sintético ya generado; 'catalog' vuelve al match guardado. */
export async function setConceptSource(
  conceptId: string,
  source: 'catalog' | 'ia',
): Promise<{ ok: boolean; source: 'catalog' | 'ia' }> {
  return await apiFetch(`/api/v1/apu-ia/concepts/${conceptId}/source`, { method: 'POST', body: { source } });
}

/** Persiste el toggle "Cotizar con IA" (marca de intención) de un concepto no
 *  generado, para que sobreviva a la navegación y el lote lo tome. $0, sin IA. */
export async function setConceptRequested(
  conceptId: string,
  requested: boolean,
): Promise<{ ok: boolean; requested: boolean }> {
  return await apiFetch(`/api/v1/apu-ia/concepts/${conceptId}/requested`, { method: 'POST', body: { requested } });
}

/** Lo mismo que `setConceptRequested` pero para MUCHOS conceptos de un tiro, en una sola
 *  petición: los controles de "todos" / "todo un grupo" tocan cientos de renglones y una
 *  petición por cada uno era inaceptable. Sin `conceptIds` = todos los del presupuesto. */
export async function setConceptsRequested(
  quoteId: string,
  requested: boolean,
  conceptIds?: string[],
  /** Enciende SOLO los `conceptIds` y apaga todo lo demás (controles por grupo). */
  exclusivo?: boolean,
): Promise<{ ok: boolean; requested: boolean; actualizados: number }> {
  return await apiFetch(`/api/v1/quotes/${quoteId}/apu-ia/requested`, {
    method: 'POST',
    body: conceptIds ? { requested, conceptIds, ...(exclusivo ? { exclusivo } : {}) } : { requested },
  });
}

/**
 * Detener la generación de sintéticos en curso. La respuesta significa "pedido
 * registrado": el worker para después de guardar el análisis que esté generando, así que
 * la pantalla debe mostrar "Deteniendo…" hasta que el progreso confirme. Lo ya generado se
 * conserva y se cobra (son análisis usables).
 */
export async function stopApuIaGeneration(quoteId: string): Promise<{ ok: boolean; runId: string; status: string }> {
  return await apiFetch(`/api/v1/quotes/${quoteId}/apu-ia/stop`, { method: 'POST' });
}

export async function listApuIaRuns(quoteId: string): Promise<{ runs: ApuIaRun[] }> {
  return await apiFetch(`/api/v1/quotes/${quoteId}/apu-ia/runs`);
}

export async function getApuIaRun(quoteId: string, runId: string): Promise<ApuIaRunDetail> {
  return await apiFetch(`/api/v1/quotes/${quoteId}/apu-ia/runs/${runId}`);
}

/** Matriz (análisis con IA) de UN concepto — para ver el análisis read-only en el
 *  presupuesto sin traer todo el run. */
export async function getApuIaConceptMatrix(quoteId: string, conceptId: string): Promise<{ concepto: ApuIaConcepto }> {
  return await apiFetch(`/api/v1/quotes/${quoteId}/apu-ia/concepts/${conceptId}/matrix`);
}

export async function getObraContext(projectId: string): Promise<ObraContextRecord> {
  return await apiFetch(`/api/v1/projects/${projectId}/obra-context`);
}

/** Marca el contexto vigente como no-actual (se re-inferirá del análisis). */
export async function deleteObraContext(projectId: string): Promise<{ ok: boolean; cleared: number }> {
  return await apiFetch(`/api/v1/projects/${projectId}/obra-context`, { method: 'DELETE' });
}

export async function approveMatrix(conceptId: string): Promise<{ ok: boolean; status: string }> {
  return await apiFetch(`/api/v1/apu-ia/matrices/${conceptId}/approve`, { method: 'POST', body: {} });
}
/** Rechaza el análisis con IA. Si el concepto tenía precio de catálogo antes, ese vuelve a
 *  quedar activo (el precio rechazado no puede seguir contando en el total) y la respuesta
 *  lo avisa con `precioDeCatalogoRestaurado`. */
export async function rejectMatrix(
  conceptId: string,
): Promise<{ ok: boolean; status: string; precioDeCatalogoRestaurado?: boolean }> {
  return await apiFetch(`/api/v1/apu-ia/matrices/${conceptId}/reject`, { method: 'POST', body: {} });
}
export async function reopenMatrix(conceptId: string): Promise<{ ok: boolean; status: string }> {
  return await apiFetch(`/api/v1/apu-ia/matrices/${conceptId}/reopen`, { method: 'POST', body: {} });
}

/** Aprueba en bulk todas las análisis con IA pending del quote. Solo staff Ecosoft. */
export async function approveAllMatrices(quoteId: string): Promise<{ ok: boolean; approved: number }> {
  return await apiFetch(`/api/v1/quotes/${quoteId}/apu-ia/matrices/approve-all`, { method: 'POST', body: {} });
}

/** Regenera UN solo concepto (resetea su apu_ia_* y encola job con conceptIds=[id]). */
export async function regenerateConcept(conceptId: string): Promise<{ runId: string; jobId: string; conceptId: string; clave: string | null; status: string }> {
  return await apiFetch(`/api/v1/apu-ia/concepts/${conceptId}/regenerate`, { method: 'POST', body: {} });
}

/** "Ajustar": recálculo determinista sin LLM al prender/apagar factores del concepto. */
export async function recomputeConcept(
  conceptId: string,
  overrides: { id: string; activo: boolean }[],
): Promise<{ jobId: string; conceptId: string; status: string }> {
  return await apiFetch(`/api/v1/apu-ia/concepts/${conceptId}/recompute`, { method: 'POST', body: { overrides } });
}

/** Edición manual de la composición del APU (2026-07-03): materiales/equipo/
 * herramienta/rendimiento. Determinista, $0, sin cuota; deja el APU aprobado. */
export interface CatalogPick {
  catalog_id: string;
  clave_catalogo: string;
  descripcion_catalogo?: string;
}

export interface MatrixEditPayload {
  materiales: Array<{
    origIndex: number | null;
    descripcion: string;
    unidad: string;
    cantidad_neta: number;
    desperdicio_pct: number;
    precio_unitario: number | null;
    /** Presente cuando el renglón nuevo se eligió del buscador de catálogos. */
    catalogPick?: CatalogPick;
  }>;
  equipo: Array<{
    origIndex: number | null;
    descripcion: string;
    /** Horas de la máquina por UNIDAD del concepto (campo vigente). */
  horas_uso_por_unidad?: number;
  /** LEGACY: horas por jornada (APU anteriores al 2026-08-11). */
  tiempo_uso_hr_por_jornada?: number;
    precio_unitario: number | null;
    catalogPick?: CatalogPick;
  }>;
  herramienta_pct?: number;
  rendimiento_base?: number;
}

export async function saveMatrixEdit(
  conceptId: string,
  edit: MatrixEditPayload,
): Promise<{ jobId: string; conceptId: string; status: string }> {
  return await apiFetch(`/api/v1/apu-ia/concepts/${conceptId}/matrix`, { method: 'PUT', body: edit });
}

export interface CatalogSearchHit {
  catalogId: string;
  clave: string;
  descripcion: string;
  unidad: string;
  precio: number | null;
  tipo: number | null;
}

/** Busca recursos en los catálogos de la cotización (FTS, $0) para el picker
 * de "agregar insumo" al editar el APU. `kind` filtra por tipo de recurso:
 * material (tipos 1/6) o equipo (tipo 3) — nunca conceptos (7). */
export async function searchQuoteCatalog(
  quoteId: string,
  q: string,
  kind: 'material' | 'equipo' = 'material',
): Promise<CatalogSearchHit[]> {
  const r = await apiFetch<{ hits: CatalogSearchHit[] }>(
    `/api/v1/quotes/${quoteId}/catalog-search?q=${encodeURIComponent(q)}&kind=${kind}`,
  );
  return r.hits;
}

export async function resolveAuxiliarReceta(
  conceptId: string,
  body: { matIdx: number; descripcion: string; unidad?: string },
): Promise<{ ok: boolean; sub_receta: Record<string, unknown>; jobId?: string }> {
  return await apiFetch(`/api/v1/apu-ia/matrices/${conceptId}/resolve-auxiliar`, { method: 'POST', body });
}

export async function putObraContext(
  projectId: string,
  body: {
    context: ObraContext;
    factores_seleccion: FactorSeleccion[];
    source?: 'deduced' | 'manual' | 'mixed';
  },
): Promise<{ id: string; ok: boolean; hadSignificantChange?: boolean; outdatedCount?: number; totalOutdated?: number }> {
  return await apiFetch(`/api/v1/projects/${projectId}/obra-context`, {
    method: 'PUT',
    body,
  });
}
